import { ToolContainer, FileDropZone, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { looksLikeVideo, probeMedia } from "./utils";
import { parseSrt, toSrt, DEFAULT_SUBTITLE_STYLE, type SubtitleStyle } from "./subtitles";
import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, fetchFile, checkFileSize, ffmpegOutputToBlob, stopProcessing, resetFFmpegEngine, burnSubtitles, addSoftSubtitles, canCopyIntoMp4 } from "./operations";
import { VideoProcessingProgress, showVideoError, FileSizeNotice } from "./uiHelpers";
import type { FFmpeg } from "@ffmpeg/ffmpeg";

const TOOL_INFO_CONTENT = {
  what: `Burns subtitle text into a video's picture (or, if you tick the option, adds it as a switchable subtitle track), using standard SRT-format timing, in English and in Hindi, Tamil, Telugu, Bengali, Gujarati, Kannada, Malayalam, Punjabi, Arabic, Hebrew, Thai and other scripts (Chinese, Japanese and Korean are not supported yet). Runs entirely in your browser — the video never leaves your device.`,
  how: `Upload your video, paste your subtitles in SRT format (the standard "1 / 00:00:00,000 --> 00:00:05,000 / Hello, world!" style), and download the video with subtitles burned in.`,
  tip: `Burning subtitles into the picture forces a full re-encode of the video, so this takes real time and burned-in subtitles can no longer be turned off or edited afterward — if you might need to change the captions later, keep your original SRT file safe as well. Some phone-recorded videos (particularly .mov files) use a codec this tool can't read — if processing fails right away, try re-exporting the video from your phone's own share options first. In the subtitle-track option, any text in angle brackets such as <Tags> is read as formatting markup and removed from the track, so write it without the brackets if it must show.`,
};

const SRT_PLACEHOLDER = "1\n00:00:00,000 --> 00:00:03,000\nHello, world!\n\n2\n00:00:03,000 --> 00:00:06,000\nThis is a second line.";

export default function SubtitleAdder() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [srtText, setSrtText] = useState("");
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<Blob | null>(null);
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [style, setStyle] = useState<SubtitleStyle>(DEFAULT_SUBTITLE_STYLE);
  const [soft, setSoft] = useState(false);
  const [srtError, setSrtError] = useState("");
  const checkSrt = (text: string) => {
    if (!text.trim()) { setSrtError(""); return; }
    try { parseSrt(text); setSrtError(""); } catch (e) { setSrtError((e as Error).message); }
  };
  const loadSrtFile = async (f: File | undefined) => {
    if (!f) return;
    const text = toSrt(await f.text());
    setSrtText(text); setResult(null); checkSrt(text);
  };

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    if (!looksLikeVideo(f)) { toast.error(`"${f.name}" isn't a video file`); return; }
    setFile(f); setPreview(URL.createObjectURL(f)); setResult(null);
  }, []);

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) stopProcessing(ffmpegRef.current);
  };

  const addSubtitles = async () => {
    if (!file) return;
    if (!srtText.trim()) { toast.error("Enter your subtitle text in SRT format first"); return; }
    try { parseSrt(srtText); } catch (e) { toast.error((e as Error).message); return; }
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Preparing the video tools...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading video and subtitle files...");
      const ext = file.name.match(/\.([^.]+)$/)?.[1] || "mp4";
      await ffmpeg.writeFile(`input.${ext}`, await fetchFile(file));

      if (soft) {
        const probe = await probeMedia(ffmpeg, `input.${ext}`);
        if (canCopyIntoMp4(probe)) {
          setStatusMessage("Adding the subtitle track...");
          const data = await addSoftSubtitles(ffmpeg, `input.${ext}`, "output.mp4", srtText, setProgress, controller.signal);
          setResult(ffmpegOutputToBlob(data, "video/mp4"));
          toast.success("Subtitle track added. Switch it on in your player (CC / Subtitles menu).");
          return;
        }
        toast.info("This video's format can't take a separate subtitle track, so the subtitles are burned into the picture instead.");
      }
      setStatusMessage("Burning in subtitles (this re-encodes the whole video)...");
      // Output is always MP4 regardless of the input's own extension --
      // burnSubtitles hardcodes libx264/aac encoding (see its own
      // comment), and libx264 isn't a codec WebM containers can hold at
      // all (WebM only supports VP8/VP9/AV1). Reusing the input's own
      // extension for the output name here used to produce a WebM
      // container with an H.264 stream inside it -- an invalid
      // combination that fails outright with "Could not write header
      // for output file" the moment someone uploads a .webm source,
      // confirmed with a real ffmpeg run, not just reasoned about.
      const data = await burnSubtitles(ffmpeg, `input.${ext}`, "output.mp4", srtText, setProgress, controller.signal, style);

      setResult(ffmpegOutputToBlob(data, "video/mp4"));
      toast.success("Subtitles added!");
    } catch (err) {
      showVideoError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="video/*" maxSize={5000} onFiles={handleFiles} label="Drop a video" description="Add subtitles and captions" />
      ) : (
        <div className="space-y-4">
          <video src={preview} controls className="w-full max-h-48 rounded-lg border" />
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}
          <div><Label>Subtitle Text (SRT format)</Label>
            <Textarea aria-label="Subtitle Text (SRT format)" value={srtText} onChange={e => { setSrtText(e.target.value); setResult(null); checkSrt(e.target.value); }} placeholder={SRT_PLACEHOLDER} className="font-mono text-sm min-h-[120px]" />
            <div className="flex items-center gap-3 mt-2 text-xs">
              <label className="underline cursor-pointer">Or upload an .srt, .vtt or .ass file<input type="file" accept=".srt,.vtt,.ass,.ssa,text/plain,text/vtt" className="hidden" onChange={e => loadSrtFile(e.target.files?.[0])} /></label>
              {srtError && <span className="text-red-600" role="alert">{srtError}</span>}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <label>Size<select className="block border rounded px-2 py-1 w-full bg-background" value={style.sizeFraction} onChange={e => setStyle(s => ({ ...s, sizeFraction: Number(e.target.value) }))}><option value={0.035}>Small</option><option value={0.05}>Medium</option><option value={0.07}>Large</option></select></label>
            <label>Position<select className="block border rounded px-2 py-1 w-full bg-background" value={style.position} onChange={e => setStyle(s => ({ ...s, position: e.target.value as SubtitleStyle["position"] }))}><option value="bottom">Bottom</option><option value="middle">Middle</option><option value="top">Top</option></select></label>
            <label>Colour<select className="block border rounded px-2 py-1 w-full bg-background" value={style.color} onChange={e => setStyle(s => ({ ...s, color: e.target.value as SubtitleStyle["color"] }))}><option value="white">White</option><option value="yellow">Yellow</option></select></label>
            <label className="flex items-end gap-1.5 pb-1 col-span-2 sm:col-span-4"><input type="checkbox" checked={soft} onChange={e => { setSoft(e.target.checked); setResult(null); }} /> Add as a subtitle track instead of burning in (takes seconds, no quality loss, can be switched off; works for MP4/MOV-type videos, style options don't apply)</label>
            <label className="flex items-end gap-1.5 pb-1"><input type="checkbox" checked={style.background} onChange={e => setStyle(s => ({ ...s, background: e.target.checked }))} /> Dark box behind text</label>
          </div>
          {processing && <VideoProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} />}
          <div className="flex gap-3">
            {result ? (
              <>
                <DownloadButton blob={result} fileName={`subtitled_${file.name.replace(/\.[^.]+$/, ".mp4")}`} />
                <Button variant="outline" onClick={() => { setFile(null); setPreview(""); setResult(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={addSubtitles} disabled={processing || sizeCheck?.severity !== "ok"}>{processing ? "Adding Subtitles..." : "Add Subtitles"}</Button>
                <Button variant="outline" onClick={() => { setFile(null); setPreview(""); }} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
